<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="600px"
    z-index="9999"
    @keydown.escape.prevent
  >
    <v-card>
      <!-- Header -->
      <v-card-title class="headline warning white--text" style="background-color: #ff9800;">
        <v-icon left>mdi-lock-alert</v-icon>
        Domicilio Bloqueado
      </v-card-title>

      <v-card-text class="pt-6">
        <!-- Mensaje de error -->
        <v-alert type="warning" outlined prominent class="mb-6">
          <v-icon left>mdi-alert-circle</v-icon>
          {{ blockedData.message }}
        </v-alert>

        <!-- Caso 1: Con domicilios disponibles -->
        <div v-if="blockedData.availableResidences && blockedData.availableResidences.length > 0">
          <p class="text-subtitle2 font-weight-bold mb-4">
            Selecciona otro domicilio para continuar:
          </p>

          <v-list outlined class="mb-6">
            <v-list-item
              v-for="residence in blockedData.availableResidences"
              :key="residence.id"
              class="my-2"
            >
              <v-list-item-avatar>
                <v-icon color="primary">mdi-home</v-icon>
              </v-list-item-avatar>

              <v-list-item-content>
                <v-list-item-title v-if="getHousingUnitName(residence)">
                  {{ getHousingUnitName(residence) }}
                </v-list-item-title>
                <v-list-item-subtitle>
                  {{ getHouseAddress(residence) }}
                </v-list-item-subtitle>
              </v-list-item-content>

              <v-list-item-action>
                <v-btn
                  small
                  color="primary"
                  :disabled="switching"
                  :loading="switchingId === residence.id"
                  @click="switchResidence(residence.id)"
                >
                  Activar
                </v-btn>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </div>

        <!-- Caso 2: Sin domicilios disponibles -->
        <div v-else>
          <v-alert type="error" outlined prominent class="mb-4">
            <v-icon left>mdi-alert</v-icon>
            No tienes otros domicilios disponibles
          </v-alert>

          <p class="text-body2 mb-6">
            Para resolver esta situación, por favor:
          </p>

          <v-list dense class="mb-6">
            <v-list-item>
              <v-list-item-icon>
                <v-icon small color="info">mdi-phone</v-icon>
              </v-list-item-icon>
              <v-list-item-title class="text-body2">
                Contacta con administración de la residencia
              </v-list-item-title>
            </v-list-item>
            <v-list-item>
              <v-list-item-icon>
                <v-icon small color="info">mdi-email</v-icon>
              </v-list-item-icon>
              <v-list-item-title class="text-body2">
                Solicita cambio de domicilio a través del portal
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </div>
      </v-card-text>

      <!-- Actions -->
      <v-card-actions v-if="blockedData.availableResidences && blockedData.availableResidences.length === 0">
        <v-spacer></v-spacer>
        <v-btn color="primary" @click="logout" :loading="loggingOut">
          Cerrar Sesión
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import residencesService from '../services/residencesService'

export default {
  name: 'BlockedResidenceDialog',
  data () {
    return {
      switching: false,
      switchingId: null,
      loggingOut: false
    }
  },
  computed: {
    ...mapGetters(['getBlockedResidenceData', 'getShowBlockedResidenceDialog', 'getResidences', 'getAvailableHouses']),
    dialog: {
      get () {
        return this.getShowBlockedResidenceDialog
      },
      set (val) {
        if (!val) {
          // No permitir cerrar el dialog de forma manual
        }
      }
    },
    blockedData () {
      return this.getBlockedResidenceData || {}
    }
  },
  watch: {
    getShowBlockedResidenceDialog (newVal) {
      if (newVal) {
        // Cerrar otros dialogs cuando se abre BlockedResidenceDialog
        this.$store.commit('dialog_show_active_false')
        if (this.blockedData.availableResidences && this.blockedData.availableResidences.length > 0) {
          this.fetchMyResidences()
          this.fetchAvailableHouses()
        }
      }
    }
  },
  methods: {
    ...mapActions(['clearBlockedResidenceDialog', 'fetchMyResidences', 'fetchAvailableHouses']),
    resolveHouse (houseIdOrObj) {
      if (!houseIdOrObj) return null
      if (typeof houseIdOrObj === 'object') return houseIdOrObj
      return this.getAvailableHouses.find(h => h.id === houseIdOrObj) || null
    },
    resolveFullResidence (residence) {
      return this.getResidences.find(r => r.id === residence.id) || residence
    },
    getHousingUnitName (residence) {
      const fullResidence = this.resolveFullResidence(residence)
      return fullResidence.idHousingUnit ? fullResidence.idHousingUnit.name : ''
    },
    getHouseAddress (residence) {
      const fullResidence = this.resolveFullResidence(residence)
      const house = this.resolveHouse(fullResidence.idHouse)
      if (!house) return 'Casa ' + fullResidence.idHouse
      return `${house.street} #${house.number}`
    },
    async switchResidence (residenceId) {
      this.switching = true
      this.switchingId = residenceId

      try {
        await residencesService.switchResidence(residenceId)

        this.$store.commit('snackbar_change_message', 'Domicilio cambiado exitosamente')
        this.$store.commit('snackbar_change_color', 'success')
        this.$store.commit('snackbar_activate_true')

        // Esperar a que desaparezca la notificación
        setTimeout(() => {
          window.location.reload()
        }, 1500)
      } catch (error) {
        this.switching = false
        this.switchingId = null

        const errorMessage = (error.response && error.response.data && error.response.data.error) || 'Error al cambiar el domicilio'
        this.$store.commit('snackbar_change_message', errorMessage)
        this.$store.commit('snackbar_change_color', 'error')
        this.$store.commit('snackbar_activate_true')
      }
    },
    async logout () {
      this.loggingOut = true
      try {
        await this.$store.dispatch('AUTH_LOGOUT')
        this.$router.push('/login')
      } catch (error) {
        this.loggingOut = false
        console.error('Error al cerrar sesión:', error)
      }
    }
  }
}
</script>

<style scoped>
.warning {
  background-color: #ff9800;
}
</style>
